import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const OAUTH_ERROR_MESSAGES = {
  oauth_error: 'Google returned an authentication error. Please try again.',
  no_code: 'No authorization code was returned. Please try signing in again.',
  invalid_state: 'Your sign-in session expired. Please try again.',
  token_exchange_failed: 'Could not complete sign-in verification. Please try again.',
  no_access_token: 'Sign-in token was missing. Please try again.',
  userinfo_failed: 'Could not retrieve your account profile. Please try again.',
  email_missing: 'Your Google account did not return an email address.',
  account_conflict: 'This email is already linked to another sign-in method. Use that method to log in.',
  user_validation_failed: 'Your account could not be validated. Please try again.',
  authentication_failed: 'OAuth authentication failed. Please try again.',
};

const OAUTH_CODE_KEYS = ['code', 'auth_code', 'oauth_code'];

const readFirstParam = (params, keys) => {
  for (const key of keys) {
    const value = params.get(key);
    if (value) {
      return value;
    }
  }
  return null;
};

const resolveOAuthCallbackParams = (searchParams) => {
  let code = readFirstParam(searchParams, OAUTH_CODE_KEYS);
  let error = searchParams.get('error');
  let provider = searchParams.get('provider');

  // Fallback to raw URL in case routing/proxy transformed callback parameters.
  if (!code && !error) {
    try {
      const currentUrl = new URL(window.location.href);
      const queryParams = currentUrl.searchParams;

      code = readFirstParam(queryParams, OAUTH_CODE_KEYS) || code;
      error = queryParams.get('error') || error;
      provider = queryParams.get('provider') || provider;

      if (!code && currentUrl.hash) {
        const rawHash = currentUrl.hash.startsWith('#') ? currentUrl.hash.slice(1) : currentUrl.hash;
        const hashQuery = rawHash.includes('?') ? rawHash.split('?')[1] : rawHash;
        const hashParams = new URLSearchParams(hashQuery);

        code = readFirstParam(hashParams, OAUTH_CODE_KEYS) || code;
        error = hashParams.get('error') || error;
        provider = hashParams.get('provider') || provider;
      }
    } catch (parseError) {
      console.warn('OAuth callback URL parse fallback failed:', parseError);
    }
  }

  return { code, error, provider };
};

const useOAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableProviders, setAvailableProviders] = useState([]);

  // Fetch available OAuth providers
  const fetchProviders = useCallback(async () => {
    try {
      const response = await api.request('/auth/oauth/providers');
      const data = await response.json();
      setAvailableProviders(data.providers || []);
    } catch (err) {
      console.error('Failed to fetch OAuth providers:', err);
      setAvailableProviders([]);
    }
  }, []);

  // Initialize OAuth login.
  // options.role ('agent' | 'lawyer') carries the account type picked on the
  // signup page; the backend stores it with the OAuth state and records it as a
  // pending request on the account after Google confirms the login.
  const initiateOAuth = useCallback((provider, options = {}) => {
    setLoading(true);
    setError(null);

    // If the app is loaded over HTTPS, never initiate OAuth against an HTTP API origin.
    const oauthBaseUrl = window.location.protocol === 'https:' && api.baseURL.startsWith('http://')
      ? api.baseURL.replace(/^http:\/\//i, 'https://')
      : api.baseURL;

    const params = new URLSearchParams();
    if (options.role) params.set('role', options.role);
    const query = params.toString();

    // Redirect to OAuth login endpoint
    window.location.href = `${oauthBaseUrl}/auth/oauth/login/${provider}${query ? `?${query}` : ''}`;
  }, []);

  // Handle OAuth callback (called from OAuth callback page)
  const handleOAuthCallback = useCallback(async (searchParams) => {
    const { code, error, provider } = resolveOAuthCallbackParams(searchParams);

    // Strip sensitive params from URL immediately to avoid Referer leaks
    if (window.history?.replaceState) {
      const cleanUrl = `${window.location.pathname}?provider=${provider || ''}`;
      window.history.replaceState({}, '', cleanUrl);
    }

    if (error) {
      const errorMessage = OAUTH_ERROR_MESSAGES[error] || OAUTH_ERROR_MESSAGES.authentication_failed;
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }

    if (code) {
      try {
        setLoading(true);
        
        // Exchange the short-lived authorization code for tokens via a secure POST
        const exchangeResponse = await api.request('/auth/secure/exchange-code', {
          method: 'POST',
          body: JSON.stringify({ code }),
        });
        
        if (!exchangeResponse.ok) {
          throw new Error('Code exchange failed');
        }
        
        const authData = await exchangeResponse.json();
        const token = authData.token;
        const userData = authData.user;
        
        setLoading(false);
        return { 
          success: true, 
          token, 
          user: userData, 
          provider 
        };
      } catch (err) {
        setError('Failed to complete authentication');
        setLoading(false);
        return { success: false, error: err.message };
      }
    }

    console.warn('OAuth callback missing authorization code and error param:', {
      href: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      provider,
    });
    setError('No authorization code received from OAuth provider');
    setLoading(false);
    return { success: false, error: 'No code received' };
  }, []);

  // Load available providers on mount
  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  return {
    loading,
    error,
    availableProviders,
    initiateOAuth,
    handleOAuthCallback,
    fetchProviders
  };
};

export default useOAuth;
